import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import { ApiErrorPayload } from "types/ApiErrorPayload";
import {
  getChangedFields,
  normalizeOptionalText,
} from "helpers/getChangedFields";
import { normalizeSeoHtml } from "helpers/seoAttributes";

export interface PageItem {
  id?: number;
  title: string;
  slug: string;
  locales: string[];
  content: string;
  effectiveFrom?: string;
  isActive: boolean | number;
}
export interface PageBunchPayload {
  ids: number[];
  isActive: number;
}
interface PagesState {
  list: PageItem[];
  activePage: number;
  totalPages: number;
  totalItems: number;
  isListFetched: boolean;
  isItemFetched: boolean;
  isItemFetching: boolean;
  selectedItems: number[];
  payloadGroup?: PageItem;
  payload?: PageItem;
  initialPayload?: PageItem;
  prosessingItemId?: PageItem["id"];
}

interface GetListPayload {
  limit: number;
  offset: number;
  title: string;
  isActive?: number;
  order: {
    title: string;
    id: string;
    isActive: string;
  };
}

const defPayload: PageItem = {
  title: "",
  slug: "",
  content: "",
  locales: [],
  isActive: true,
  effectiveFrom: "",
};

const initialState: PagesState = {
  list: [],
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  isListFetched: false,
  isItemFetched: false,
  isItemFetching: false,
  selectedItems: [],
  payload: defPayload,
};

export const getPagesList = createAsyncThunk(
  "cms-pages/list",
  async (data: GetListPayload) => {
    try {
      const result = await instance.post(`${apiRoutes.pagesList}`, data);
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);
export const createPage = createAsyncThunk<
  number,
  PageItem,
  { rejectValue: ApiErrorPayload }
>("cms-pages/create", async (data: PageItem, { rejectWithValue }) => {
  try {
    const response = await instance.post(`${apiRoutes.pagesCreate}`, {
      ...data,
      isActive: data.isActive ? 1 : 0,
    });
    return response.data.id;
  } catch (error: any) {
    const apiError = error?.data?.error;

    if (apiError?.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});

export const updatePage = createAsyncThunk<
  boolean,
  PageItem,
  { rejectValue: ApiErrorPayload }
>("cms-pages/update", async (data: PageItem, { rejectWithValue, getState }) => {
  const state = getState() as PagesState | { "cms-pages": PagesState };
  const initialPayload =
    "cms-pages" in state
      ? state["cms-pages"].initialPayload
      : state.initialPayload;
  const changes = getChangedFields(
    initialPayload,
    data,
    ["title", "slug", "content", "locales", "effectiveFrom", "isActive"],
    {
      title: normalizeOptionalText,
      slug: normalizeOptionalText,
      content: normalizeSeoHtml,
      isActive: (value) => (value ? 1 : 0),
    },
  );
  try {
    await instance.put(`${apiRoutes.pagesUpdate}/${data.id}`, {
      ...changes,
    });
    return true;
  } catch (error: any) {
    const data = error?.data;
    const apiError = data?.error ?? data;

    if (apiError && apiError.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});

export const updatePageBunch = createAsyncThunk(
  "cms-pages/update-bunch",
  async (data: PageBunchPayload) => {
    try {
      await instance.put(`${apiRoutes.updatePagesBunch}`, {
        ...data,
        isActive: data.isActive,
      });
      return true;
    } catch (error) {
      return false;
    }
  },
);
export const deletePage = createAsyncThunk(
  "cms-pages/delete",
  async (id: string | number) => {
    try {
      await instance.delete(`${apiRoutes.pagesDelete}/${id}`);
      return true;
    } catch (error) {
      return false;
    }
  },
);

export const getPage = createAsyncThunk(
  "cms-pages/read",
  async (id: string | number) => {
    try {
      const result = await instance.get(`${apiRoutes.pagesItem}/${id}`);
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const cmsPagesSlice = createSlice({
  name: "cms-pages",
  initialState,
  reducers: {
    handlePaginate: (state: PagesState, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
      state.selectedItems = [];
    },
    handleUpdateFields: (
      state: PagesState,
      action: PayloadAction<PageItem>,
    ) => {
      state.payload = { ...state.payload, ...action.payload };
    },
    handleClearFields: (state: PagesState) => {
      state.payload = defPayload;
      state.initialPayload = undefined;
      // state.payloadGroup = undefined;
      state.isItemFetched = false;
    },
    handleSelectItem: (state: PagesState, action: PayloadAction<number>) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItems = [...state.selectedItems, action.payload];
      }
    },
    handleSelectAll: (state: PagesState) => {
      if (state.selectedItems.length === state.list.length) {
        state.selectedItems = [];
      } else {
        state.selectedItems = state.list.map((el) => el.id ?? 0);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getPagesList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 1;
      state.totalPages = action.payload?.totalPages ?? 1;
    });
    builder.addCase(createPage.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(createPage.fulfilled, (state) => {
      state.isListFetched = false;
      state.isItemFetching = false;
    });
    builder.addCase(createPage.rejected, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(updatePage.pending, (state, action) => {
      state.isItemFetching = true;
      state.prosessingItemId = action.meta.arg.id;
    });
    builder.addCase(updatePage.fulfilled, (state, action) => {
      state.list = state.list.map((item) =>
        item.id === action.meta.arg.id
          ? {
              ...item,
              ...Object.fromEntries(
                Object.keys(item)
                  .filter(
                    (field) =>
                      field in action.meta.arg &&
                      action.meta.arg[field as keyof PageItem] !== undefined,
                  )
                  .map((field) => [
                    field,
                    action.meta.arg[field as keyof PageItem],
                  ]),
              ),
            }
          : item,
      );
      state.isItemFetching = false;
      state.prosessingItemId = undefined;
      state.initialPayload = state.payload ? { ...state.payload } : undefined;
    });
    builder.addCase(updatePage.rejected, (state) => {
      state.isItemFetching = false;
      state.prosessingItemId = undefined;
    });
    builder.addCase(updatePageBunch.fulfilled, (state) => {
      state.isListFetched = false;
      state.selectedItems = [];
    });
    builder.addCase(deletePage.pending, (state, action) => {
      state.prosessingItemId = +action.meta.arg;
    });
    builder.addCase(deletePage.fulfilled, (state) => {
      state.isListFetched = false;
      state.prosessingItemId = undefined;
    });
    builder.addCase(deletePage.rejected, (state) => {
      state.prosessingItemId = undefined;
    });
    builder.addCase(getPage.fulfilled, (state, action) => {
      if (action.payload) {
        state.payload = action.payload;
        state.initialPayload = { ...action.payload };
        state.isItemFetched = true;
      } else {
        state.isItemFetched = false;
      }
    });
  },
});

export const {
  handlePaginate,
  handleSelectItem,
  handleSelectAll,
  handleUpdateFields,
  handleClearFields,
} = cmsPagesSlice.actions;
